import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getProgramLockStatus,
  lockProgramData,
  unlockProgramData,
  bulkLockPrograms,
  getLockedProgramsReport
} from '../services/rpc';
import type {
  ProgramLockActionResponse,
  ProgramLockMetadata,
  ProgramLockStatus,
  ProgramBulkLockResult
} from '../types/rpc-contracts';

export const programLockQueryKey = (programId: string) => ['program-lock-status', programId] as const;
export const lockedProgramsReportKey = ['program-lock-status', 'report'] as const;

interface LockParams {
  reason: string;
  metadata?: ProgramLockMetadata;
  operatorId?: string | null;
}

interface UnlockParams {
  reason: string;
  metadata?: ProgramLockMetadata;
  operatorId?: string | null;
}

export function useProgramLock(programId: string | null) {
  const queryClient = useQueryClient();

  const lockStatusQuery = useQuery<ProgramLockStatus>({
    enabled: Boolean(programId),
    queryKey: programId ? programLockQueryKey(programId) : ['program-lock-status', 'noop'],
    queryFn: () => {
      if (!programId) {
        return Promise.resolve({
          program_id: '',
          data_locked: false,
          lock_metadata: {}
        } satisfies ProgramLockStatus);
      }
      return getProgramLockStatus(programId);
    }
  });

  const lockMutation = useMutation<ProgramLockActionResponse, unknown, LockParams>({
    mutationFn: async ({ reason, metadata, operatorId }) => {
      if (!programId) {
        throw new Error('Program id is required to lock data');
      }
      return await lockProgramData(programId, reason, metadata ?? {}, operatorId ?? null);
    },
    onSuccess: () => {
      if (programId) {
        void queryClient.invalidateQueries({ queryKey: programLockQueryKey(programId) });
        void queryClient.invalidateQueries({ queryKey: lockedProgramsReportKey });
      }
    }
  });

  const unlockMutation = useMutation<ProgramLockActionResponse, unknown, UnlockParams>({
    mutationFn: async ({ reason, metadata, operatorId }) => {
      if (!programId) {
        throw new Error('Program id is required to unlock data');
      }
      return await unlockProgramData(programId, reason, operatorId ?? null, metadata ?? {});
    },
    onSuccess: () => {
      if (programId) {
        void queryClient.invalidateQueries({ queryKey: programLockQueryKey(programId) });
        void queryClient.invalidateQueries({ queryKey: lockedProgramsReportKey });
      }
    }
  });

  return {
    lockStatus: lockStatusQuery.data,
    isLoading: lockStatusQuery.isLoading,
    isLocked: lockStatusQuery.data?.data_locked ?? false,
    lockError: lockStatusQuery.error,
    refetchLock: lockStatusQuery.refetch,
    lockProgram: lockMutation.mutateAsync,
    unlocking: unlockMutation.isPending,
    locking: lockMutation.isPending,
    unlockProgram: unlockMutation.mutateAsync,
    lockMutation,
    unlockMutation
  };
}

export function useBulkProgramLock() {
  const queryClient = useQueryClient();

  const mutation = useMutation<ProgramBulkLockResult, unknown, { programIds: string[]; reason: string; metadata?: ProgramLockMetadata; operatorId?: string | null; }>(
    {
      mutationFn: ({ programIds, reason, metadata, operatorId }) =>
        bulkLockPrograms(programIds, reason, metadata ?? {}, operatorId ?? null),
      onSuccess: (result, variables) => {
        result.results.forEach((res) => {
          const pid = res.data?.program_id ?? null;
          if (pid) {
            void queryClient.invalidateQueries({ queryKey: programLockQueryKey(pid) });
          }
        });
        if (variables.programIds.length) {
          void queryClient.invalidateQueries({ queryKey: lockedProgramsReportKey });
        }
      }
    }
  );

  return mutation;
}

export function useLockedProgramsReport(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  return useQuery<ProgramLockStatus[]>({
    queryKey: lockedProgramsReportKey,
    enabled,
    queryFn: () => getLockedProgramsReport()
  });
}
